import { cva, type VariantProps } from 'class-variance-authority';
import { ReactNode } from 'react';

import { cn } from '../../../libs/utils';

const MAIN_TITLE_FONT = {
  large: 'heading-large-bold',
  medium: 'heading-medium-bold',
  small: 'heading-small-bold',
  xsmall: 'body-large-bold'
};

const DESCRIPTION_FONT = {
  large: 'body-large-regular',
  medium: 'body-medium-regular',
  small: 'body-small-regular',
  xsmall: 'caption-small-regular'
};

const MAIN_TITLE_SIZE = {
  large: 'pt-spacious-small pb-normal-large gap-normal-small',
  medium: 'pt-normal-large pb-normal-medium gap-normal-small',
  small: 'pt-normal-large pb-normal-medium gap-tiny-large',
  xsmall: 'pt-normal-medium pb-normal-small gap-tiny-small'
};

const titleVariants = cva('flex flex-col whitespace-nowrap', {
  variants: {
    size: { ...MAIN_TITLE_SIZE }
  },
  defaultVariants: {
    size: 'medium'
  }
});

export interface MainTitleProps extends VariantProps<typeof titleVariants> {
  className?: string;
  title: ReactNode;
  titleAs?: 'h1' | 'h2' | 'h3' | 'div';
  description?: ReactNode;
  graphic?: ReactNode;
  size?: keyof typeof MAIN_TITLE_SIZE;
}

export const MainTitle = ({ className, size = 'medium', title, titleAs: TitleTag = 'div', description, graphic, ...props }: MainTitleProps) => {
  return (
    <div className={titleVariants({ size, className })} {...props}>
      {graphic}
      <TitleTag data-testid="mainTitle" className={cn(MAIN_TITLE_FONT[size], 'whitespace-pre-wrap break-words text-neutral-foreground-main')}>
        {title}
      </TitleTag>
      {description && (
        <div data-testid="mainTitleDescription" className={cn(DESCRIPTION_FONT[size], 'whitespace-pre-wrap break-words text-neutral-foreground-low')}>
          {description}
        </div>
      )}
    </div>
  );
};
